import { A2uiLitElement } from '@a2ui/lit/v0_9';
import type { ComponentApi } from '@a2ui/web_core/v0_9';
import type { PropertyValues } from 'lit';

/**
 * Base for our exercise components: renders into the light DOM so the app's
 * global stylesheet applies, and offers a one-liner to send an action back
 * to the tutor.
 */
export abstract class ExerciseElement<Api extends ComponentApi> extends A2uiLitElement<Api> {
  protected createRenderRoot() {
    return this;
  }

  private contentKey = '';

  /**
   * The surface reuses element instances between renders, so a flipped flashcard would stay flipped
   * when the next card arrives. Subclasses reset their local state in `resetState()`; we call it
   * whenever the props change.
   */
  willUpdate(changed: PropertyValues) {
    super.willUpdate(changed);
    const key = JSON.stringify(this.controller?.props ?? null);
    if (key !== this.contentKey) {
      const first = this.contentKey === '';
      this.contentKey = key;
      if (!first) this.resetState();
    }
  }

  protected resetState() {}

  /** Send a named action with a context object; the surface's action handler forwards it to the model. */
  protected send(name: string, context: Record<string, unknown> = {}) {
    return this.context?.dispatchAction({ event: { name, context } });
  }
}

/**
 * Normalise an answer for lenient comparison (accents kept, case/punctuation ignored).
 * Same stripping as the server's normaliseKey — client and server keys must match.
 * Persian-aware like the server: ZWNJ/ZWJ fold to space, Arabic kaf/yeh fold to
 * Persian forms, Persian punctuation/diacritics strip.
 */
export function normalise(s: string) {
  return s
    .toLowerCase()
    .replace(/\u0643/g, '\u06A9')
    .replace(/\u064A/g, '\u06CC')
    .replace(/[\u200C\u200D]/g, ' ')
    .replace(/[.,!?¡¿;:"'«»()—–\u061F\u060C\u061B-]/g, '')
    .replace(/\u0640/g, '')
    .replace(/[\u064B-\u065F]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
